import { useRef, type Dispatch } from "react";
import type { Order, OrderTask } from "../../lib/types.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { useMontiertRef } from "../../state/useMontiertRef.ts";
import { isPaymentRequestTask } from "../../lib/payments.ts";
import { BESTELLUNG_WEG_MELDUNG, mitBestellbezug } from "../../lib/bestellkennung.ts";
import { KonfliktFehler } from "../../api/basis.ts";
import {
  bestellungLaden,
  NachladenNachSchreibenFehler,
  SSE_ENTITAET as BESTELLUNG_ENTITAET,
} from "../../api/bestellungen.ts";
import {
  aufgabeAendern, aufgabeAnlegen, aufgabeLoeschen, aufgabenBulkErledigen,
  vorlageAufBestellungAnwenden,
} from "../../api/aufgaben.ts";

/**
 * R4b (Fehleranzeige Paket 2, Task 10, R-T10a): scheitern beim Sammelloeschen ALLE DELETEs, und mindestens eines nicht
 * mit einem Versionskonflikt (403, Netz, 5xx), steht neben dem Eintrag der Loeschung ein zweiter, wenn auch das Nachladen
 * scheitert. Es sind zwei unabhaengige Fehlschlaege mit je eigener Ursache; keiner behauptet einen Stand.
 */
const STAND_NICHT_GELADEN_MELDUNG = "Der aktuelle Stand der Aufgaben konnte nicht geladen werden. Bitte neu laden.";

/**
 * R-T10a: scheitern ALLE DELETEs und alle mit einem Versionskonflikt, folgt aus dem Nachladen eine Aussage ueber den
 * Stand - dann steht GENAU EIN Eintrag da, erst nach dem Nachladen: `KEINE_GELOESCHT_KONFLIKT_MELDUNG` plus
 * `STAND_GELADEN_MELDUNG` bzw. `STAND_KONFLIKT_NICHT_GELADEN_MELDUNG`. (Bewusst nicht "nicht mehr aktuell": das ist der Wortlaut
 * eines R4-Satzes, hier wurde aber nichts gespeichert.)
 */
const KEINE_GELOESCHT_KONFLIKT_MELDUNG =
  "Keine der markierten Aufgaben konnte gelöscht werden – sie wurden zwischenzeitlich geändert.";
const STAND_GELADEN_MELDUNG = "Der aktuelle Stand ist geladen – bitte prüfen.";
const STAND_KONFLIKT_NICHT_GELADEN_MELDUNG = "Der aktuelle Stand konnte nicht geladen werden – bitte neu laden.";

/**
 * Ausgang von `aufgabenNachladen`: der Stand wurde geladen und eingespielt, die Bestellung gibt es nicht mehr (`weg`: das
 * Laden lieferte `null`, nichts wurde eingespielt, der Drawer zeigt den alten Stand - R-T11f), das Laden scheiterte mit einer
 * Ursache, oder es wurde verworfen, weil der Drawer inzwischen weitergezogen ist (kein Fehler, nichts geladen, nichts zu
 * melden).
 */
type NachladeAusgang =
  | { art: "geladen" }
  | { art: "weg" }
  | { art: "gescheitert"; ursache: unknown }
  | { art: "verworfen" };

interface DrawerTasksEingabe {
  drawerId: string | null;
  bestellungen: readonly Order[];
  rechte: ReadonlySet<string>;
  versende: Dispatch<Aktion>;
  zeigeMeldung: (text: string) => void;
  drawerAendern: (id: string, aenderung: Record<string, unknown>, onErfolg?: () => void, onFehler?: (fehler?: unknown) => void, istAktuell?: () => boolean) => void;
  drawerArchivUmschalten: (id: string) => void;
  nachladeFehlerMeldung: string;
}

export function useDrawerTasks(eingabe: DrawerTasksEingabe) {
  const {
    versende, zeigeMeldung, drawerAendern,
    nachladeFehlerMeldung: NACHLADEN_FEHLGESCHLAGEN_MELDUNG,
  } = eingabe;
  const aktuelleEingabe = useRef(eingabe);
  aktuelleEingabe.current = eingabe;
  const istEingehaengt = useMontiertRef("passiv");
  const drawerKontext = useRef({ id: eingabe.drawerId, folge: 0 });
  if (drawerKontext.current.id !== eingabe.drawerId) {
    drawerKontext.current = { id: eingabe.drawerId, folge: drawerKontext.current.folge + 1 };
  }

  // Ein A -> B -> A-Wechsel invalidiert den alten A-Aufruf ebenfalls. Die Unterressource darf
  // fertig schreiben; ihre spaete Antwort darf aber keinen neuen Drawer oder Store veraendern.
  function aktuellerVorgang(): () => boolean {
    const folge = drawerKontext.current.folge;
    return () => istEingehaengt.current && drawerKontext.current.folge === folge;
  }

  /**
   * R3 (Fehleranzeige Paket 1): ERFOLGE eines nicht mehr offenen Drawers duerfen verworfen werden, FEHLER nie.
   * Ist der Drawer weitergezogen, sieht der Nutzer die Bestellung nicht mehr; dann nennt der Kontext sie.
   * `aktuelleEingabe` haelt auch nach dem Unmount den letzten Stand der Bestellungen.
   */
  function kontextFuer(id: string, satz: string, istAktuell: () => boolean): string {
    if (istAktuell()) return satz;
    const bestellung = aktuelleEingabe.current.bestellungen.find((b) => b.id === id);
    return mitBestellbezug(satz, bestellung, id);
  }

  /**
   * Aufgaben sind eine Unterressource; nach jedem Schreiben wird die ganze Bestellung ersetzt.
   * Den frischen Datensatz direkt weiterreichen: der Store-Dispatch rendert nicht synchron.
   *
   * Meldet NICHTS, sondern liefert den Ausgang (`NachladeAusgang`; loest nie ab) - was daraus zu sagen ist, haengt vom
   * Aufrufer ab (Fehleranzeige Paket 2, Task 10): nach einem Schreiben, das GELANG, ist es ein R4-Satz
   * (`aufgabenNachladenUndMelden`); nach einem Sammelloeschen, bei dem NICHTS geloescht wurde, wuerde "Gespeichert ..." luegen,
   * dort entscheidet `drawerAufgabenSammelLoeschen` (R4b).
   */
  function aufgabenNachladen(
    id: string,
    istAktuell: () => boolean,
    beiFrischemStand?: (neu: Order) => void,
  ): Promise<NachladeAusgang> {
    if (!istAktuell()) return Promise.resolve({ art: "verworfen" });
    return bestellungLaden(id)
      .then((neu): NachladeAusgang => {
        if (!istAktuell()) return { art: "verworfen" };
        if (neu === null) return { art: "weg" };
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
        beiFrischemStand?.(neu);
        return { art: "geladen" };
      })
      .catch((fehler: unknown): NachladeAusgang => {
        console.error(`Bestellung "${id}" konnte nach der Aufgabenaenderung nicht geladen werden:`, fehler);
        return { art: "gescheitert", ursache: fehler };
      });
  }

  /**
   * Der R4-Weg nach einem Schreiben, das GELANG: nachladen; scheitert es, meldet diese Funktion es selbst (im Fehlerstapel,
   * auch nach einem Drawer-Wechsel - R3) und ergibt false, damit kein Vorlagen-Erfolg folgt. Die Aufgabe ist geschrieben,
   * nur die Anzeige veraltet (R-Z2): kein Wiederholungsrat der Ursache hinter dem Satz.
   */
  function aufgabenNachladenUndMelden(
    id: string,
    istAktuell: () => boolean,
    beiFrischemStand?: (neu: Order) => void,
  ): Promise<boolean> {
    return aufgabenNachladen(id, istAktuell, beiFrischemStand).then((ausgang) => {
      if (ausgang.art === "gescheitert") {
        meldeNachladeFehler(ausgang.ursache, kontextFuer(id, NACHLADEN_FEHLGESCHLAGEN_MELDUNG, istAktuell));
      }
      // `weg` (die Bestellung gibt es nicht mehr, nach einem Schreiben, das GELANG) blieb immer ohne Meldung und ohne
      // Fehlschlag fuer den Aufrufer: der Vorlagen-Erfolg folgt wie bisher.
      return ausgang.art === "geladen" || ausgang.art === "weg";
    });
  }

  /** Gemeinsamer Schreiben -> Nachladen-Weg; Schreibfehler und Nachladefehler bleiben getrennt. */
  function schreibenUndNachladen<T>(
    id: string,
    schreiben: Promise<T>,
    istAktuell: () => boolean,
    fehlerMeldung: string,
    beiFrischemStand?: (neu: Order) => void,
    beiErfolg?: (ergebnis: T, nachgeladen: boolean) => void,
  ): Promise<void> {
    return schreiben
      .then((ergebnis) => aufgabenNachladenUndMelden(id, istAktuell, beiFrischemStand).then((nachgeladen) => {
        if (istAktuell()) beiErfolg?.(ergebnis, nachgeladen);
      }))
      .catch((fehler: unknown) => {
        console.error(`Aufgabenschreibvorgang der Bestellung "${id}":`, fehlerMeldung, fehler);
        meldeFehler(fehler, kontextFuer(id, fehlerMeldung, istAktuell));
      });
  }

  /** Keine geratene Version: lokale Aufgaben ohne Server-rowVersion sind nicht schreibbar. */
  function versionVon(aufgabe: OrderTask): number | null {
    const roh = (aufgabe as { rowVersion?: unknown }).rowVersion;
    return typeof roh === "number" && Number.isInteger(roh) ? roh : null;
  }

  function gespeicherteAufgabe(aufgabe: OrderTask, aktion?: "geändert" | "gelöscht") {
    const version = versionVon(aufgabe);
    const taskId = aufgabe.id as string | undefined;
    if (version === null || !taskId) {
      console.error("Aufgabe ohne id/rowVersion - sie stammt nicht vom Server:", aufgabe);
      if (aktion) meldeFehler(`Diese Aufgabe ist noch nicht gespeichert und kann nicht ${aktion} werden.`);
      return null;
    }
    return { taskId, version };
  }

  /** addTask (index.html:9158): beiErfolg erst nach dem Nachladen der Aufgabenliste. */
  function drawerAufgabeAnlegen(id: string, aufgabe: { title: string }, beiErfolg: (neu: OrderTask) => void): void {
    void schreibenUndNachladen(id, aufgabeAnlegen(id, aufgabe), aktuellerVorgang(),
      "Die Aufgabe konnte nicht angelegt werden.",
      undefined, (neu) => beiErfolg(neu));
  }

  /** patchTask/patchOpenTaskDetail (index.html:9172, 9033): eigene Aufgaben-Version. */
  function drawerAufgabeAendern(
    id: string,
    aufgabe: OrderTask,
    aenderung: Record<string, unknown>,
  ): void {
    const istAktuell = aktuellerVorgang();
    const gespeichert = gespeicherteAufgabe(aufgabe, "geändert");
    if (!gespeichert) return;
    const { taskId, version } = gespeichert;
    void schreibenUndNachladen(id, aufgabeAendern(id, taskId, aenderung, version), istAktuell,
      "Die Aufgabe konnte nicht geändert werden.",
      (neu) => { if (aenderung.done === true) alleAufgabenNachfragenUndArchivieren(id, neu); });
  }

  /** Ref-Sperre je Bestellung: wirkt auch gegen mehrere Klicks im selben Tick. */
  const zahlungAnstossenLaeuft = useRef<Set<string>>(new Set());

  /**
   * Extras und Zahlungsauftrag laufen nacheinander. Scheitern die Extras, wird keine Aufgabe
   * geschrieben. Beide Wege duerfen nicht unabhaengig nachladen: sonst ueberschriebe die spaetere
   * Extras-Antwort den neuen Task im Store und ein weiterer Klick koennte einen zweiten anlegen.
   * Die Ref-Sperre bleibt bis zum Ende der gesamten Kette bestehen und faellt auch bei Fehlern.
   */
  function drawerZahlungAnstossen(
    id: string,
    aenderung: Record<string, unknown>,
    aufgabe: { title: string; description: string; due: string },
  ): void {
    const istAktuell = aktuellerVorgang();
    if (zahlungAnstossenLaeuft.current.has(id)) return;
    zahlungAnstossenLaeuft.current.add(id);
    const freigeben = (): void => {
      zahlungAnstossenLaeuft.current.delete(id);
    };
    const vorhandenerZahlungsauftrag = (): OrderTask | undefined => {
      const bestellung = aktuelleEingabe.current.bestellungen.find((b) => b.id === id);
      return ((bestellung?.tasks as OrderTask[] | undefined) || []).find((t) => isPaymentRequestTask(t));
    };
    drawerAendern(
      id,
      aenderung,
      () => {
        if (!istAktuell()) { freigeben(); return; }
        const vorhanden = vorhandenerZahlungsauftrag();
        if (!vorhanden) {
          void schreibenUndNachladen(id, aufgabeAnlegen(id, { ...aufgabe, kind: "payment_request", phaseId: "custom" }), istAktuell,
            "Der Zahlungsauftrag konnte nicht angelegt werden.")
            .finally(freigeben);
          return;
        }
        const gespeichert = gespeicherteAufgabe(vorhanden, "geändert");
        if (!gespeichert) { freigeben(); return; }
        const { taskId, version } = gespeichert;
        void schreibenUndNachladen(id, aufgabeAendern(id, taskId,
          { name: aufgabe.title, description: aufgabe.description, due: aufgabe.due, done: false }, version), istAktuell,
          "Der Zahlungsauftrag konnte nicht aktualisiert werden.")
          .finally(freigeben);
      },
      (fehler) => {
        freigeben();
        // Zwei Ausgaenge, in denen der Extras-PATCH gelang, `onErfolg` oben aber nie laeuft - der Zahlungsauftrag
        // entsteht dann nicht, und `drawerAendern` sagt nichts darueber:
        // - R-T6c: das Nachladen nach dem PATCH scheiterte (`NachladenNachSchreibenFehler`; `drawerAendern` meldet
        //   nur "gespeichert ... Bitte neu laden"). Ohne diesen Satz hielte der Nutzer den Zahlungsauftrag fuer angelegt.
        // - R-T6g: der Nutzer hat den Drawer gewechselt (oder die App ist weg); `drawerAendern` verwirft den Erfolg
        //   und ruft `onFehler` OHNE Fehler. Die Kette laeuft bewusst nicht weiter; der Satz nennt die Bestellung.
        // Bei jedem anderen Fehlschlag sagt `drawerAendern` selbst, dass die Aenderung scheiterte.
        if (fehler === undefined || fehler instanceof NachladenNachSchreibenFehler) {
          const ergebnis = vorhandenerZahlungsauftrag() ? "aktualisiert" : "angelegt";
          meldeFehler(kontextFuer(
            id, `Der Zahlungsauftrag wurde nicht ${ergebnis} – bitte nach dem Neuladen erneut anstoßen.`, istAktuell,
          ));
        }
      },
      istAktuell,
    );
  }

  /** Nach dem letzten Einzel-Abhaken nur mit edit_orders fragen; der Nutzer archiviert bewusst. */
  function alleAufgabenNachfragenUndArchivieren(id: string, bestellung: Order): void {
    if (!hatRecht(aktuelleEingabe.current.rechte, "edit_orders")) return;
    if (bestellung.archived) return;
    const tasks = ((bestellung as { tasks?: unknown }).tasks as OrderTask[] | undefined) || [];
    // Eine taskleere Bestellung fragt nie (leere Liste ist keine "alle erledigt"-Aussage), ebenso
    // wenig eine mit mindestens einer offenen Aufgabe.
    if (tasks.length === 0 || !tasks.every((t) => !!t.done)) return;
    if (!confirm("Alle Aufgaben sind erledigt. Bestellung jetzt archivieren?")) return;
    aktuelleEingabe.current.drawerArchivUmschalten(id);
  }

  /** removeTask (index.html:9188): DELETE mit Aufgaben-Version, danach Bestellung nachladen. */
  function drawerAufgabeLoeschen(id: string, aufgabe: OrderTask): void {
    const istAktuell = aktuellerVorgang();
    const gespeichert = gespeicherteAufgabe(aufgabe, "gelöscht");
    if (!gespeichert) return;
    const { taskId, version } = gespeichert;
    void schreibenUndNachladen(id, aufgabeLoeschen(id, taskId, version), istAktuell,
      "Die Aufgabe konnte nicht gelöscht werden.");
  }

  /**
   * R-T10a: der EINE Eintrag, wenn beim Sammelloeschen alle DELETEs mit Versionskonflikt scheiterten und das Nachladen
   * ausgegangen ist. Geladen: der Stand stimmt, der Nutzer soll nur nachsehen. Gescheitert: die Ursache des Nachladens
   * steht dahinter (`meldeFehler`; ein Wiederholungsrat ist hier harmlos, es wurde nichts geschrieben).
   *
   * Weg (R-T11f; Re-Review Task 10, M1): das Nachladen lieferte `null`, die Bestellung gibt es nicht mehr. Es wurde nichts
   * eingespielt, der Drawer zeigt die alten Aufgaben - "Der aktuelle Stand ist geladen" waere gelogen. GENAU EIN Eintrag,
   * `BESTELLUNG_WEG_MELDUNG`.
   *
   * Ohne Aussage ueber den Stand, nur der neutrale Konfliktsatz mit der Bestellung als Bezug, steht der Eintrag in zwei Faellen:
   * - Verworfen (R-T10b): der Drawer ist weitergezogen, es wurde nicht nachgeladen - ein Satz ueber den Stand waere erfunden.
   * - Gescheitert OHNE meldbaren Text (R-T10c: Abbruch, 401; `fehlertext` ergibt `null`): "Abbruch und 401 bleiben still"
   *   (L6) gilt fuer den LADEfehler. Die gescheiterte LOESCHUNG meldet R4b immer; nur der Nachsatz zum Stand haengt am
   *   Nachladen. Der Eintrag stimmt auch nach einer erneuten Anmeldung noch.
   */
  function meldeKonfliktNachNachladen(id: string, istAktuell: () => boolean, stand: NachladeAusgang): void {
    const neutral = (): void => meldeFehler(kontextFuer(id, KEINE_GELOESCHT_KONFLIKT_MELDUNG, istAktuell));
    switch (stand.art) {
      case "geladen":
        meldeFehler(kontextFuer(id, `${KEINE_GELOESCHT_KONFLIKT_MELDUNG} ${STAND_GELADEN_MELDUNG}`, istAktuell));
        return;
      case "weg":
        meldeFehler(kontextFuer(id, BESTELLUNG_WEG_MELDUNG, istAktuell));
        return;
      case "gescheitert": {
        const grund = fehlertext(stand.ursache);
        if (grund === null) {
          neutral();
          return;
        }
        meldeFehler(
          grund,
          kontextFuer(id, `${KEINE_GELOESCHT_KONFLIKT_MELDUNG} ${STAND_KONFLIKT_NICHT_GELADEN_MELDUNG}`, istAktuell),
        );
        return;
      }
      case "verworfen":
        neutral();
        return;
    }
  }

  /**
   * Einzel-DELETEs parallel, danach genau ein Nachladen. Ungespeicherte Tasks vorher aussortieren.
   * Teilfehler unterscheiden Versionskonflikte von anderen Fehlern (403/Netz); kein erfundener
   * Aktualitaetskonflikt bei anderen Fehlern. Bei keinem gueltigen Task wird nicht nachgeladen.
   *
   * R4b (Fehleranzeige Paket 2, Task 10): scheitern ALLE DELETEs, wurde nichts gespeichert - scheitert danach auch das
   * Nachladen, steht kein "Gespeichert - die Ansicht ist aber nicht mehr aktuell" da (so lautete der Eintrag bis dahin, und
   * das stimmte nicht). Was stattdessen dasteht, haengt von der Ursache der DELETEs ab (R-T10a):
   * - Alle mit Versionskonflikt: erst nachladen, dann GENAU EIN Eintrag (`meldeKonfliktNachNachladen`). Aus dem Konflikt
   *   folgt eine Aussage ueber den Stand ("zwischenzeitlich geaendert"), und die haengt am Ausgang des Nachladens; zwei
   *   Eintraege liessen den ersten schon etwas ueber den Stand sagen, bevor der zweite das Gegenteil melden koennte.
   * - Mindestens ein DELETE mit anderer Ursache (403, Netz, 5xx): zwei unabhaengige Fehlschlaege mit je eigener Ursache,
   *   "Keine der markierten Aufgaben konnte geloescht werden." mit der Ursache der DELETEs und - nur wenn auch das
   *   Nachladen scheitert - "Der aktuelle Stand der Aufgaben konnte nicht geladen werden. Bitte neu laden." mit der des
   *   Nachladens. Kein Eintrag behauptet einen Stand (das R4b-Verbot wird eingehalten); der zweite sagt nur, dass
   *   nachzuladen misslang, und das ist unabhaengig davon, warum die Loeschung scheiterte.
   * Ist wenigstens ein DELETE gelungen, ist "Gespeichert" wahr und bleibt (R4); die Teilmeldung steht daneben.
   */
  function drawerAufgabenSammelLoeschen(id: string, aufgaben: OrderTask[]): void {
    const istAktuell = aktuellerVorgang();
    const gueltig: Array<{ taskId: string; version: number }> = [];
    for (const aufgabe of aufgaben) {
      const gespeichert = gespeicherteAufgabe(aufgabe);
      if (gespeichert) gueltig.push(gespeichert);
    }
    if (!gueltig.length) {
      meldeFehler("Keine der markierten Aufgaben konnte gelöscht werden.");
      return;
    }
    Promise.allSettled(
      gueltig.map((eintrag) => aufgabeLoeschen(id, eintrag.taskId, eintrag.version)),
    ).then((ergebnisse) => {
      // Kein `istAktuell`-Abbruch hier (R3): ein Teilfehlschlag nach einem Drawer-Wechsel bleibt sichtbar. Das
      // Nachladen selbst ueberspringt `aufgabenNachladen` bei einem weitergezogenen Drawer.
      const fehlgeschlagene = ergebnisse.filter(
        (ergebnis): ergebnis is PromiseRejectedResult => ergebnis.status === "rejected",
      );
      const fehlgeschlagen = fehlgeschlagene.length;
      if (fehlgeschlagen > 0) {
        console.error(
          `${fehlgeschlagen} von ${gueltig.length} Aufgaben der Bestellung "${id}" konnten nicht geloescht werden:`,
          fehlgeschlagene.map((e) => e.reason),
        );
      }
      // Siehe Kopfkommentar: nur echte Versionskonflikte heissen "nicht mehr aktuell".
      const sonstigeFehler = fehlgeschlagene.filter((e) => !(e.reason instanceof KonfliktFehler));
      // Die Ursache, die der Nutzer braucht, ist die erste NICHT-Konflikt-Ursache (403, Netz, 5xx); gibt es
      // keine, ist es ein reiner Versionskonflikt und der feste Satz sagt alles.
      const ursache = (sonstigeFehler[0] ?? fehlgeschlagene[0])?.reason;
      if (fehlgeschlagen === gueltig.length) {
        // R4b: NICHTS geloescht - das Nachladen ist hier ein reiner Ladeweg, kein R4 ("Gespeichert ..." waere gelogen).
        void aufgabenNachladen(id, istAktuell).then((stand) => {
          if (sonstigeFehler.length === 0) {
            // R-T10a: nur Versionskonflikte - aus dem Nachladen folgt eine Aussage ueber den Stand, deshalb steht erst
            // danach GENAU EIN Eintrag da (`meldeFehler`: nichts geschrieben, der Rat der Ursache stimmt).
            meldeKonfliktNachNachladen(id, istAktuell, stand);
            return;
          }
          // Andere Ursache: zwei unabhaengige Fehlschlaege mit je eigener Ursache. Der Eintrag des Nachladens kommt zuerst,
          // der der Loeschung liegt dann oben im Stapel (neueste zuerst).
          if (stand.art === "gescheitert") {
            meldeFehler(stand.ursache, kontextFuer(id, STAND_NICHT_GELADEN_MELDUNG, istAktuell));
          }
          meldeFehler(ursache, kontextFuer(id, "Keine der markierten Aufgaben konnte gelöscht werden.", istAktuell));
        });
        return;
      }
      aufgabenNachladenUndMelden(id, istAktuell).then(() => {
        if (fehlgeschlagen === 0) return;
        const erfolgreich = gueltig.length - fehlgeschlagen;
        if (sonstigeFehler.length === 0) {
          // kein-r4: Teilerfolg (ein Teil der Aufgaben ist geloescht) - "nicht mehr aktuell" nennt die Aufgaben mit Versionskonflikt, die nicht geloescht wurden, nicht die Anzeige; keine Ursache, kein Nachladen-Satz
          meldeFehler(kontextFuer(
            id, `${erfolgreich} von ${gueltig.length} Aufgaben gelöscht - ${fehlgeschlagen} nicht mehr aktuell.`, istAktuell,
          ));
          return;
        }
        meldeFehler(ursache, kontextFuer(
          id,
          `${erfolgreich} von ${gueltig.length} Aufgaben gelöscht - ${fehlgeschlagen} ${
            fehlgeschlagen === 1 ? "konnte" : "konnten"
          } nicht gelöscht werden.`,
          istAktuell,
        ));
      });
    });
  }

  /** Bulk-Phase ohne Einzelversionen; fehlende IDs ueberspringt die API. Keine Archivabfrage. */
  function drawerPhaseErledigen(id: string, taskIds: string[], erledigt: boolean): void {
    void schreibenUndNachladen(id, aufgabenBulkErledigen(id, taskIds, erledigt), aktuellerVorgang(),
      "Die Phase konnte nicht geändert werden.");
  }

  /**
   * Der TasksTab haelt seine Sperre bis diese Promise aufloest, auch bei Fehlern.
   * Erfolg nur nach erfolgreichem Nachladen melden; null und hinzugefuegt=0 bleiben gueltig.
   */
  function drawerVorlageAnwenden(id: string): Promise<void> {
    return schreibenUndNachladen(id, vorlageAufBestellungAnwenden(id), aktuellerVorgang(),
      "Die Vorlage konnte nicht angewendet werden.",
      undefined, (ergebnis, nachgeladen) => {
        if (!nachgeladen) return;
        zeigeMeldung(ergebnis.hinzugefuegt > 0
          ? `Vorlage angewendet · ${ergebnis.hinzugefuegt} neue ${ergebnis.hinzugefuegt === 1 ? "Aufgabe" : "Aufgaben"}`
          : "Vorlage war bereits vollständig angewendet");
      });
  }

  return {
    drawerAufgabeAnlegen, drawerAufgabeAendern, drawerAufgabeLoeschen,
    drawerAufgabenSammelLoeschen, drawerPhaseErledigen, drawerVorlageAnwenden, drawerZahlungAnstossen,
  };
}
